import { h } from 'preact';
import Field from '../../FormFields/Field';
import StateField from './StateField';
import CountryField from './CountryField';
import { AddressStateError, AddressType, FieldContainerProps } from '../types';
import { useCoreContext } from '../../../../core/Context/CoreProvider';
import Language from '../../../../language/Language';
import InputText from '../../FormFields/InputText';
import { AutocompleteValue } from '../../FormFields/types';
import { ADDRESS_FIELD_TOKEN_MAP } from '../constants';

function getErrorMessage(errors: AddressStateError, fieldName: string, i18n: Language, label: string): string | boolean {
    if (typeof errors[fieldName]?.errorMessage === 'object') {
        const { translationKey, translationObject } = errors[fieldName].errorMessage;
        return i18n.get(translationKey, translationObject);
    }
    // Default error msg: Enter the [label name]
    const errorMsg = i18n.get(errors[fieldName]?.errorMessage, { values: { label: label.toLowerCase() } });
    return errorMsg || !!errors[fieldName];
}

/**
 * USAGE: Specifically defined as a util to provide a wrapper for fields created within the Address component
 *
 * NOT TO BE USED: if you just want to add a Country or State dropdown outside of an Address component
 * - then you should implement <CountryField> or <StateField> directly
 */
function getAddressAutocomplete(fieldName: string, addressType: AddressType): AutocompleteValue {
    const token = ADDRESS_FIELD_TOKEN_MAP[fieldName];
    if (!token) return undefined;
    if (addressType === 'billingAddress') return `billing ${token}` as AutocompleteValue;
    if (addressType === 'deliveryAddress') return `shipping ${token}` as AutocompleteValue;
    // Fallback: bare token (no prefix) when address type is unspecified
    return token as AutocompleteValue;
}

function FieldContainer(props: Readonly<FieldContainerProps>) {
    const { i18n } = useCoreContext();
    const { classNameModifiers = [], data, errors, valid, fieldName, onInput, onBlur, trimOnBlur, maxLength, disabled, addressType } = props;

    const value: string = data[fieldName];
    const selectedCountry: string = data.country;
    const isOptional: boolean = props.specifications.countryHasOptionalField(selectedCountry, fieldName);
    const labelKey: string = props.specifications.getKeyForField(fieldName, selectedCountry);
    const optionalLabel = isOptional ? ` ${i18n.get('field.title.optional')}` : '';
    const label = `${i18n.get(labelKey)}${optionalLabel}`;
    const errorMessage = getErrorMessage(errors, fieldName, i18n, label);

    switch (fieldName) {
        case 'country':
            return (
                <CountryField
                    allowedCountries={props.allowedCountries}
                    classNameModifiers={classNameModifiers}
                    label={label}
                    errorMessage={errorMessage}
                    onDropdownChange={props.onDropdownChange}
                    value={value}
                    required={!isOptional}
                />
            );
        case 'stateOrProvince':
            return (
                <StateField
                    classNameModifiers={classNameModifiers}
                    label={label}
                    errorMessage={errorMessage}
                    onDropdownChange={props.onDropdownChange}
                    selectedCountry={selectedCountry}
                    specifications={props.specifications}
                    value={value}
                    required={!isOptional}
                />
            );
        default:
            return (
                <Field
                    label={label}
                    classNameModifiers={classNameModifiers}
                    errorMessage={errorMessage}
                    isValid={valid[fieldName]}
                    name={fieldName}
                    i18n={i18n}
                    onFocus={e => props.onFieldFocusAnalytics(fieldName, e)}
                    onBlur={e => props.onFieldBlurAnalytics(fieldName, e)}
                >
                    <InputText
                        name={fieldName}
                        classNameModifiers={classNameModifiers}
                        value={value}
                        onInput={onInput}
                        onBlur={onBlur}
                        maxlength={maxLength}
                        trimOnBlur={trimOnBlur}
                        disabled={disabled}
                        required={!isOptional}
                        autocomplete={getAddressAutocomplete(fieldName, addressType)}
                    />
                </Field>
            );
    }
}

export default FieldContainer;
